import type { Customer, Reservation } from '../types';
import { customerService as supabaseCustomerService } from './supabaseService';
import localDatabase from './localDatabase';
import { isWalkInSystemCustomer } from '../lib/customerClassification';
import { customerMatchesSearch } from '../lib/utils';

// Define the structure for paginated API responses
export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

// Define the shape of data for creating or updating a customer
export interface CustomerData {
  name: string;
  phone: string;
  notes?: string;
}

class CustomerServiceSupabase {
  /**
   * Fetches a paginated and searchable list of customers.
   * @param page - The page number to fetch.
   * @param limit - The number of customers per page.
   * @param search - A search term to filter customers by name or phone.
   * @returns A promise that resolves to a paginated list of customers.
   */
  async getCustomers(tenantId: string, page = 1, limit = 20, search = ''): Promise<PaginatedResponse<Customer>> {
    try {
      const pending = await localDatabase.getPendingSyncItems().catch(() => []);
      const pendingDeleteIds = new Set(
        (pending as any[]).filter(p => p.table_name === 'customers' && p.operation === 'DELETE').map(p => p.record_id)
      );
      let localWalkInCount = 0;
      try {
        const localAll = await localDatabase.getAllCustomers();
        localWalkInCount = localAll.filter(
          (c) => c.tenant_id === tenantId && isWalkInSystemCustomer(c as any),
        ).length;
      } catch {
        localWalkInCount = 0;
      }

      // Accent-insensitive search: load full directory, match in memory, then paginate (same rules as floor plan / forms).
      if (search.trim()) {
        const allRows = (await supabaseCustomerService.listAllCustomersWithStats(
          tenantId,
        )) as unknown as Customer[];
        if (allRows.length > 0) {
          try {
            await localDatabase.bulkUpsertCustomers(allRows as any);
          } catch (syncError) {
            console.warn('Failed to sync customers to local database:', syncError);
          }
        }
        const filteredData = allRows.filter(
          (c) =>
            !pendingDeleteIds.has(c.id) &&
            !isWalkInSystemCustomer(c) &&
            customerMatchesSearch(c, search),
        );
        const total = filteredData.length;
        const pages = Math.max(1, Math.ceil(total / limit));
        const currentPage = Math.min(Math.max(1, page), pages);
        const start = (currentPage - 1) * limit;
        const data = filteredData.slice(start, start + limit);

        return {
          data,
          pagination: {
            page: currentPage,
            limit,
            total,
            pages,
          },
        };
      }

      const result = await supabaseCustomerService.getCustomers(tenantId, page, limit, search);
      const filteredData = result.data.filter(
        (c) => !pendingDeleteIds.has(c.id) && !isWalkInSystemCustomer(c),
      );
      const adjustedTotal = Math.max(
        0,
        (result.total || 0) - pendingDeleteIds.size - localWalkInCount,
      );
      const adjustedPages = Math.max(1, Math.ceil(adjustedTotal / limit));

      if (filteredData.length > 0) {
        try {
          await localDatabase.bulkUpsertCustomers(filteredData as any);
        } catch (syncError) {
          console.warn('Failed to sync customers to local database:', syncError);
        }
      }

      return {
        data: filteredData,
        pagination: {
          page: Math.min(page, adjustedPages),
          limit,
          total: adjustedTotal,
          pages: adjustedPages
        }
      };
    } catch (error) {
      console.error('Error in getCustomers (falling back to local cache):', error);
      // Offline/local fallback using IndexedDB
      // IMPORTANT: Only use local data if we have a valid tenantId to ensure tenant isolation
      if (!tenantId) {
        console.warn('⚠️ Cannot fallback to local cache - no tenantId provided');
        throw error;
      }
      
      try {
        console.log('🔄 Using offline data for customers...');
        const allCustomers = await localDatabase.getAllCustomers();
        // Filter by tenant_id to ensure we only return data for the current tenant
        const tenantFiltered = allCustomers.filter(
          (c) => c.tenant_id === tenantId && !isWalkInSystemCustomer(c as any),
        );
        const filtered = search.trim()
          ? tenantFiltered.filter((c) => customerMatchesSearch(c, search))
          : tenantFiltered;

        const total = filtered.length;
        const pages = Math.max(1, Math.ceil(total / limit));
        const currentPage = Math.min(Math.max(1, page), pages);
        const start = (currentPage - 1) * limit;
        const end = start + limit;
        const data = filtered.slice(start, end) as unknown as Customer[];

        return {
          data,
          pagination: {
            page: currentPage,
            limit,
            total,
            pages,
          },
        };
      } catch (fallbackError) {
        console.error('Local cache fallback failed:', fallbackError);
        throw error;
      }
    }
  }

  /**
   * Fetches a single customer by their ID.
   * @param customerId - The ID of the customer to fetch.
   * @returns A promise that resolves to the customer's data.
   */
  async getCustomerById(tenantId: string, customerId: string): Promise<Customer> {
    try {
      // Try Supabase first
      const customer = await supabaseCustomerService.getCustomer(customerId, tenantId);

      // Compute reservation stats from local data so the detail modal has accurate counts
      try {
        const customerReservations = await localDatabase.getReservationsByCustomer(customerId);
        const total = customerReservations.length;
        const confirmed = customerReservations.filter(r => r.status === 'confirmed' || r.status === 'completed' || (r as any).status === 'seated').length;
        const cancelled = customerReservations.filter(r => r.status === 'cancelled' || r.status === 'no-show').length;
        (customer as any).reservation_count = total;
        (customer as any).confirmed_reservations = confirmed;
        (customer as any).cancelled_reservations = cancelled;
      } catch (statsError) {
        console.warn('Failed to compute local reservation stats for customer', customerId, statsError);
      }
      
      // Sync to local database (bulk upsert avoids adding to sync queue)
      try {
        await localDatabase.bulkUpsertCustomers([customer] as any);
      } catch (syncError) {
        console.warn('Failed to sync customer to local database:', syncError);
      }

      return customer;
    } catch (error) {
      console.error(`Error fetching customer ${customerId} (fallback to local):`, error);
      // Offline/local fallback
      const local = await localDatabase.getCustomer(customerId);
      if (local) return local as unknown as Customer;
      throw error;
    }
  }

  /**
   * Creates a new customer.
   * @param customerData - The data for the new customer.
   * @returns A promise that resolves to the newly created customer's data.
   */
  async createCustomer(tenantId: string, customerData: CustomerData): Promise<Customer> {
    try {
      // Try Supabase first
      const customer = await supabaseCustomerService.createCustomer({ ...customerData, tenant_id: tenantId });
      
      // Sync to local database (bulk upsert preserves Supabase ID and avoids sync queue)
      try {
        await localDatabase.bulkUpsertCustomers([customer] as any);
      } catch (error) {
        console.warn('Failed to sync new customer to local database:', error);
      }

      return customer;
    } catch (error) {
      console.error('Error creating customer:', error);
      throw error;
    }
  }

  /**
   * Updates an existing customer.
   * @param customerId - The ID of the customer to update.
   * @param updates - An object containing the fields to update.
   * @returns A promise that resolves to the updated customer's data.
   */
  async updateCustomer(tenantId: string, customerId: string, updates: Partial<CustomerData>): Promise<Customer> {
    try {
      // Try Supabase first
      const customer = await supabaseCustomerService.updateCustomer(customerId, { ...updates, tenant_id: tenantId } as any);
      
      // Sync to local database (bulk upsert preserves Supabase ID and avoids sync queue)
      try {
        await localDatabase.bulkUpsertCustomers([customer] as any);
      } catch (error) {
        console.warn('Failed to sync updated customer to local database:', error);
      }

      return customer;
    } catch (error) {
      console.error(`Error updating customer ${customerId}:`, error);
      throw error;
    }
  }

  /**
   * Deletes a customer.
   * @param customerId - The ID of the customer to delete.
   * @returns A promise that resolves when the customer is deleted.
   */
  async deleteCustomer(tenantId: string, customerId: string): Promise<void> {
    try {
      // Try Supabase first
      await supabaseCustomerService.deleteCustomer(customerId, tenantId);
      
      // Remove from local database (skipSync=true because we already deleted from Supabase)
      try {
        await localDatabase.deleteCustomer(customerId, true);
      } catch (error) {
        console.warn('Failed to remove customer from local database:', error);
      }
    } catch (error) {
      console.error(`Error deleting customer ${customerId}:`, error);
      throw error;
    }
  }

  /**
   * Fetches the reservation history for a specific customer.
   * @param customerId - The ID of the customer.
   * @param page - The page number for pagination.
   * @param limit - The number of reservations per page.
   * @returns A promise that resolves to a paginated list of reservations.
   */
  async getCustomerReservations(customerId: string, page = 1, limit = 10): Promise<PaginatedResponse<Reservation>> {
    try {
      // For now, we'll use the local database for customer reservations
      // This can be enhanced later with Supabase queries
      const all = await localDatabase.getReservationsByCustomer(customerId);

      // Enrich with table data so UI can show table numbers instead of raw IDs
      let tableMap = new Map<string, any>();
      try {
        const tables = await localDatabase.getAllTables();
        tableMap = new Map(tables.map(t => [t.id, t]));
      } catch (tableErr) {
        console.warn('Failed to load tables for reservation enrichment', tableErr);
      }

      const enriched = (all as any[]).map(res => {
        const table = res.table_id ? tableMap.get(res.table_id) : undefined;
        return { ...res, table };
      }) as unknown as Reservation[];

      // Sort newest first for consistent display
      enriched.sort((a, b) => new Date(`${b.date}T${b.time || '00:00'}`).getTime() - new Date(`${a.date}T${a.time || '00:00'}`).getTime());

      const total = enriched.length;
      const pages = Math.max(1, Math.ceil(total / limit));
      const currentPage = Math.min(Math.max(1, page), pages);
      const start = (currentPage - 1) * limit;
      const end = start + limit;
      
      return {
        data: enriched.slice(start, end),
        pagination: {
          page: currentPage,
          limit,
          total,
          pages,
        }
      };
    } catch (error) {
      console.error(`Error fetching reservations for customer ${customerId}:`, error);
      throw error;
    }
  }

  /**
   * Get customer by phone number (useful for reservation forms)
   * @param phone - The phone number to search for
   * @returns A promise that resolves to the customer data or null if not found
   */
  async getCustomerByPhone(tenantId: string, phone: string): Promise<Customer | null> {
    try {
      // Try Supabase first
      const customer = await supabaseCustomerService.getCustomerByPhone(phone, tenantId);
      
      if (customer) {
        // Sync to local database (bulk upsert preserves Supabase ID and avoids sync queue)
        try {
          await localDatabase.bulkUpsertCustomers([customer] as any);
        } catch (syncError) {
          console.warn('Failed to sync customer to local database:', syncError);
        }
      }

      return customer;
    } catch (error) {
      console.error(`Error fetching customer by phone ${phone} (fallback to local):`, error);
      // Offline/local fallback - ensure tenant isolation
      if (!tenantId) {
        console.warn('⚠️ Cannot fallback to local cache - no tenantId provided');
        return null;
      }
      
      try {
        const allCustomers = await localDatabase.getAllCustomers();
        // Filter by tenant_id to ensure we only return data for the current tenant
        const customer = allCustomers.find(
          (c) =>
            c.phone === phone &&
            c.tenant_id === tenantId &&
            !isWalkInSystemCustomer(c as any),
        );
        return customer as unknown as Customer || null;
      } catch (fallbackError) {
        console.error('Local cache fallback failed:', fallbackError);
        return null;
      }
    }
  }
}

export const customerService = new CustomerServiceSupabase();
